import { useEcranAide, useDemandeEcran } from './aide/AideContexte';
import React, { useState, useEffect, useCallback } from 'react';
import { stageAPI } from '../services/api';
import StageCertifProcessus from './StageCertifProcessus';
import StageCertifModeles from './StageCertifModeles';
import StageCertifAttestations from './StageCertifAttestations';

/**
 * SUIVI DE STAGE — Certification › Paramétrage (lot C.0)
 * =======================================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § 2 Paramétrage
 * Maquette : #certif-param — trois sous-onglets :
 *   Processus           ✅ lot C.0
 *   Modèles de courriel ✅ lot C.0 bis (mêmes règles que Stagiaires → Contacts, C33)
 *   Attestations        ✅ lot C.0 bis (introduction, libellés, avertissement)
 *
 * ⚠️ `recharger` ne prend AUCUN rappel de parent dans ses dépendances (défaut du
 * lot 1.3 bis du module Étudiants : la saisie en cours s'effaçait). Les écritures
 * renvoient la liste à jour, posée par `fusionner` sans relire le serveur.
 */

const SOUS_ONGLETS = [
    { cle: 'processus',    libelle: 'Processus',           icone: 'fa-diagram-project' },
    { cle: 'modeles',      libelle: 'Modèles de courriel', icone: 'fa-envelope' },
    { cle: 'attestations', libelle: 'Attestations',        icone: 'fa-stamp' }
];

// Lot L1a (E169) : `onglet` piloté par la vignette (plus de pastilles), `processusId` limite « Le processus » à CE processus.
function StageCertifParametrage({ departementId, anneeId, modifiable, onglet = null, processusId = null, onEcrit = null, onSupprime = null, manquants = [] }) {
    const [sousOngletLibre, setSousOnglet] = useState('processus');
    const sousOnglet = onglet && onglet !== 'dash' ? onglet : sousOngletLibre;
    // Aide contextuelle (A1)
    useEcranAide('certification/param/' + sousOnglet);
    useDemandeEcran('certification/param', (s) => { if (['processus', 'modeles', 'attestations'].includes(s)) setSousOnglet(s); });
    const [donnees, setDonnees]       = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur]         = useState(null);

    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true);
        setErreur(null);
        try {
            const r = await stageAPI.getCertifParametrage(departementId, anneeId, processusId);
            setDonnees(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message
                || 'Lecture du paramétrage de la certification impossible');
            setDonnees(null);
        } finally {
            setChargement(false);
        }
    }, [departementId, anneeId, processusId]);

    useEffect(() => { recharger(); }, [recharger]);

    const fusionner = useCallback((morceau) => {
        setDonnees((precedent) => (precedent ? { ...precedent, ...morceau } : precedent));
    }, []);

    return (
        <>
            {/* L2b (E170) : dans une vignette, courriels et textes d'attestation sont ceux de CE processus — on le dit. */}
            {onglet && onglet !== 'processus' && processusId && (
                <div className="stg-encart stg-encart-info stg-bandeau-propre">
                    <i className="fa-solid fa-link" />
                    <span>
                        <b>{onglet === 'modeles' ? 'Ces courriels' : 'Ces textes'} sont ceux de
                        « {(donnees?.processus || []).find((p) => p.id === processusId)?.titre || 'ce processus'} »</b> — les modifier ne
                        change <b>aucun autre processus</b>. Copiés à la création (de ceux du département, ou d'un autre processus) ;
                        « Récupérer N-1 » les recopie. Une campagne lancée garde ceux avec lesquels elle a commencé.
                    </span>
                </div>
            )}
            {!onglet && (
            <div className="param-subtab-bar" role="tablist" aria-label="Sous-onglets du paramétrage de la certification">
                {SOUS_ONGLETS.map((s) => (
                    <button key={s.cle} type="button" role="tab" aria-selected={sousOnglet === s.cle}
                            className={'param-subtab' + (sousOnglet === s.cle ? ' active' : '')}
                            onClick={() => setSousOnglet(s.cle)}>
                        <i className={'fa-solid ' + s.icone} /> {s.libelle}
                    </button>
                ))}
            </div>
            )}

            {erreur && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{erreur}</span>
                </div>
            )}

            {chargement && !donnees && (
                <div className="stg-attente">
                    <i className="fa-solid fa-circle-notch stg-tourne" />
                    <div>Lecture du paramétrage…</div>
                </div>
            )}

            {donnees && sousOnglet === 'processus' && (
                <StageCertifProcessus
                    donnees={donnees}
                    departementId={departementId}
                    anneeId={donnees.annee?.id}
                    modifiable={modifiable}
                    onMiseAJour={fusionner}
                    seulement={processusId}
                    manquants={manquants}
                    onEcrit={onEcrit}
                    onSupprime={onSupprime}
                />
            )}

            {donnees && sousOnglet === 'modeles' && (
                <StageCertifModeles donnees={donnees} departementId={departementId}
                                    modifiable={modifiable} onMiseAJour={fusionner} processusId={processusId} />
            )}

            {/* Les libellés enregistrés ici reviennent aussitôt dans les info-bulles
                de l'éditeur de processus : même objet `donnees.reglages`. */}
            {donnees && sousOnglet === 'attestations' && (
                <StageCertifAttestations donnees={donnees} departementId={departementId}
                                         modifiable={modifiable} onMiseAJour={fusionner} processusId={processusId} />
            )}
        </>
    );
}

export default StageCertifParametrage;
